/* eslint-disable @typescript-eslint/no-unused-vars */

import { endlessOfferAssets } from "assets/cdn";
import {
	CardHeader,
	Container,
	ItemImage,
	Items,
	LockImage,
	LoyalBox,
	LoyalBoxWrap,
	RewardAmount,
	RewardItem,
	Ribbon,
	RibbonText,
	StyledButton,
	StyledLoyaltyPoints
} from "./styles";
import crownV2 from "assets/crownV2.png";
import { crownWhiteIconPath } from "utils/svgIcons";
import DebugCopyIdButton from "debug/components/DebugCopyIdButton";
import { sortBundleRewards } from "helpers/rewardSort";
import useRewardsById from "hooks/useRewardsById";
import {
	EndlessNodeProps,
	EndlessOfferNode,
	EndlessOfferResponse
} from "interfaces/endless-offer";
import {
	useEnvContext,
	useGoogleAnalyticsContext,
	useWebStoreContext
} from "contexts";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import { useAppDispatch, useImagePath } from "hooks";
import { featureTrackerAPI, useClaimRewardMutation } from "redux/apis";
import { useCallback, useEffect } from "react";
import { onOpenPopUp } from "redux/slices";
import { WsDialogs } from "interfaces";
import Footer from "./lib/Footer";
import { FeaturesFlags } from "helpers";
import { getCount, getDisplay, getTimeLeft } from "utils/time";

export const EndlessNode: React.FC<EndlessNodeProps> = ({
	className,
	node,
	offer,
	isFirstNode,
	isFetching,
	backgroundUrl,
	ribbonUrl,
	backgroundLockedUrl,
	from
}) => {
	const displayRewards = useRewardsById(offer?.rewardId || node?.rewardId);
	const {
		authToken,
		handleErrorResponse,
		nowTime,
		postUserMetadata,
		suppressOrderConfirmation,
		wsStoreBuy
	} = useWebStoreContext();

	const dispatch = useAppDispatch();
	const { getImage } = useImagePath("cdn");
	const lockIcon = getImage(endlessOfferAssets.node.padlock);
	const { openDialog } = useDialogModalRedux();
	const { isFeatureFlagEnabled } = useEnvContext();
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();

	const [
		ClaimReward,
		{ isLoading: IsClaimLoading, isSuccess: IsClaimSuccess }
	] = useClaimRewardMutation();
	const { data }: { data?: EndlessOfferResponse } =
		featureTrackerAPI.endpoints.getFeatureTracker.useQueryState({
			FeatureType: "endless-offer",
			isAuthenticated: !!authToken?.value
		});

	const invalidateFeatureTracker = () => {
		dispatch(
			featureTrackerAPI.util.invalidateTags([
				{
					type: "featureTrackerTag",
					id: "endless-offer"
				}
			])
		);
	};

	const onClickBuy = async (node: EndlessOfferNode) => {
		if (!authToken?.value) {
			return;
		}

		if (!offer) {
			await ClaimReward({
				eventId: node.eventId,
				nodeIndex: node.nodeIndex
			})
				.unwrap()
				.then(() => dispatch(onOpenPopUp("endless-offer-reward-collected")))
				.catch((error) => {
					handleErrorResponse({ response: error });
					invalidateFeatureTracker();
				});
		} else {
			const invoice = await wsStoreBuy({
				offerId: node?.offerId || "",
				endlessOfferEventId: node.eventId,
				endlessOfferNodeIndex: node.nodeIndex
			});
			if (invoice) {
				const timestampMs = new Date().getTime();
				openDialog(WsDialogs.XSOLLA_PURCHASE, {
					xsollaToken: invoice.xsollaToken,
					xsollaHost: invoice.xsollaHost,
					invoice,
					section: "shop",
					source: "endlessoffer",
					pricePoint: offer?.price,
					triggerType: displayRewards?.[0]?.id,
					items: displayRewards,
					suppressOrderConfirmation,
					postUserMetadata,
					onPurchaseStarted: (data) => {
						const minutesLeft = getTimeLeft(nowTime, offer.endTime);
						reportEvent("purchase_started", {
							section_name: "limited_deals",
							offer_id: offer.id,
							price_in_cents: offer?.price * 100,
							invoice_id: invoice?.id,
							paystation_id: data?.payStation
						});
						reportPlaygamiEvent("tp.web_ga.purchase_flow", {
							event_name: "purchase_started",
							event_type: "web_paystation_purchase",
							action: "purchase_started",
							section_name: "limited_deals",
							purchase_flow_id: data?.payStation,
							session_engaged: authToken?.value ? "1" : "0",
							price_in_cents: Math.round(invoice.price * 100),
							page_location: window.location.href,
							engagement_time_msec: (
								Number(data?.data?.milliseconds) - timestampMs
							).toString(),
							reward_id: invoice?.rewardId,
							offer_id: invoice?.offerId,
							ignore_referrer:
								process.env.REACT_APP_WITH_DEBUG === "true" ? "true" : "false",
							minutes_left: minutesLeft,
							page_title: location.pathname.replace("/", ""),
							page_referrer: invoice?.invoiceMetadata?.referer,
							invoice_id: invoice?.id
						});
					},
					onPurchaseSuccess: (data) => {
						dispatch(onOpenPopUp("endless-offer-reward-collected"));
						const minutesLeft = getTimeLeft(nowTime, offer.endTime);
						reportEvent("purchase_success", {
							invoice_id: invoice?.id
						});
						reportPlaygamiEvent("tp.web_ga.purchase_flow", {
							event_name: "purchase_success",
							event_type: "web_paystation_purchase",
							action: "purchase_success",
							section_name: "limited_deals",
							purchase_flow_id: data?.version,
							session_engaged: authToken?.value ? "1" : "0",
							price_in_cents: Math.round(invoice.price * 100),
							page_location: window.location.href,
							engagement_time_msec: (
								Number(data?.milliseconds) - timestampMs
							).toString(),
							reward_id: invoice?.rewardId,
							offer_id: invoice?.offerId,
							ignore_referrer:
								process.env.REACT_APP_WITH_DEBUG === "true" ? "true" : "false",
							minutes_left: minutesLeft,
							page_title: location.pathname.replace("/", ""),
							page_referrer: invoice?.invoiceMetadata?.referer,
							invoice_id: invoice?.id,
							transaction_id: data?.purchase_invoice_id?.toString()
						});
					}
				});
			} else {
				invalidateFeatureTracker();
			}
		}
	};

	useEffect(() => {
		if (isFirstNode && data) {
			const timeRemaining = getCount(nowTime, data?.tracker?.endTime);
			const timeToDisplay = getDisplay(timeRemaining);
			const eventSource = from === "elite" ? "vip_shop" : "endless_offer";

			reportEvent("feature_rendered", {
				source: eventSource,
				reward_id: node.rewardId,
				offer_id: node.offerId,
				offer_price: offer?.price,
				node_type: node.isSuperOffer ? "super" : "normal",
				node_index: node.nodeIndex,
				configNodeCount: data?.tracker?.configNodeCount,
				nodeBadgeText: node.nodeBadgeText,
				event_timer_remaining: timeToDisplay
			});
			const timeLeft = getTimeLeft(nowTime, data?.tracker?.endTime, "seconds");
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "offer_rendered",
				event_type: "endless_offer",
				action: "offer_rendered",
				source: eventSource,
				offer_id: node?.offerId,
				reward_id: node?.rewardId,
				segment_id: data?.tracker?.eventId,
				price_in_cents: offer?.price * 100,
				value_int: offer?.loyaltyPointReward,
				time_left: timeLeft,
				tag_text: data?.tracker?.titleImageUrl,
				link_url: data?.tracker?.themeBackgroundImageUrl,
				node_type: node?.isSuperOffer ? "golden" : "normal",
				node_type2: node?.nodeIndex.toString(),
				confignodecount: data?.tracker?.configNodeCount,
				progress_status: node?.nodeBadgeText,
				theme_id: data?.tracker?.themeId
			});
		}
	}, [isFirstNode]);

	return (
		<Container
			className={className}
			image={backgroundUrl}
			isFirstNode={isFirstNode}
			lockedImage={!isFirstNode ? backgroundLockedUrl : undefined}
		>
			<CardHeader className={className}>
				{node.nodeBadgeText && (
					<Ribbon
						style={{ paintOrder: "stroke fill" }}
						image={ribbonUrl}
						isSuperOffer={node.isSuperOffer}
						className={className}
					>
						<RibbonText>{node?.nodeBadgeText}</RibbonText>
					</Ribbon>
				)}
			</CardHeader>
			<Items className={className}>
				{sortBundleRewards(displayRewards).map((displayReward) => (
					<RewardItem
						key={displayReward.id}
						//width={"50%"}
						filter={isFirstNode ? 0 : 1}
					>
						<ItemImage
							className={className}
							src={displayReward.imageUrl}
							alt=""
						/>
						<RewardAmount
							style={{ paintOrder: "stroke fill" }}
							className={className}
						>
							{displayReward.description}
						</RewardAmount>
					</RewardItem>
				))}
			</Items>

			<Footer className={className}>
				<LoyalBoxWrap className={className} sx={{ fontSize: "1.2em" }}>
					{offer && (
						<>
							{isFeatureFlagEnabled(FeaturesFlags.UI_V2) ? (
								<StyledLoyaltyPoints>
									<div>
										Earn
										<img
											src={crownV2}
											alt="crown-icon"
											style={{ width: "1em" }}
										/>
										{Number(offer.loyaltyPointReward).toLocaleString("en-US")}
									</div>
								</StyledLoyaltyPoints>
							) : (
								<LoyalBox className={className}>
									Earn
									<img
										src={crownWhiteIconPath}
										alt="crown-icon"
										style={{
											margin: "0 .33em",
											height: "1em",
											width: "auto"
										}}
									/>
									{offer.loyaltyPointReward}
								</LoyalBox>
							)}
						</>
					)}
				</LoyalBoxWrap>

				<StyledButton
					variant="contained"
					className={className}
					color="success"
					size="large"
					disabled={!isFirstNode}
					style={{
						pointerEvents: isFetching || IsClaimLoading ? "none" : "auto",
						zIndex: isFirstNode ? 6 : 2
						//isFetching || IsClaimLoading || IsClaimSuccess ? "none" : "auto"
					}}
					onClick={() => onClickBuy(node)}
				>
					{offer ? offer.price + " USD" : "FREE"}
					{!isFirstNode && <LockImage src={lockIcon}></LockImage>}
				</StyledButton>
			</Footer>
			<DebugCopyIdButton
				id={offer?.id || node?.rewardId}
				label={offer?.id ? "OfferID" : "RewardID"}
				side="center"
				vertical="top"
				style={{ top: "-1.5em", fontSize: "1.1em", zIndex: 4 }}
			/>
		</Container>
	);
};

export default EndlessNode;
